/* eslint-disable */
import React, { useState, useEffect, useRef } from 'react'
import {
    X,
    Megaphone,
    Award,
    AlertCircle,
    Sparkles,
    BookOpen,
    FileText,
    FolderOpen,
} from 'lucide-react'
import axios from 'axios'
import './NotificationDrawer.css'

function NotificationItem({ notif, onMarkSeen, onDelete, courses = [] }) {
    const [details, setDetails] = useState(null)
    const [loading, setLoading] = useState(false)

    useEffect(() => {
        if (
            notif._id &&
            !notif._id.startsWith('assignment-') &&
            notif._id !== 'welcome-notification' &&
            notif.referenceId
        ) {
            setLoading(true)
            axios
                .get(
                    `${process.env.REACT_APP_BASE_URL}/v3/notifications/${notif._id}/details`,
                    { withCredentials: true }
                )
                .then((res) => {
                    setDetails(res.data.details)
                    setLoading(false)
                })
                .catch((err) => {
                    console.error('Error fetching notification details:', err)
                    setLoading(false)
                })
        }
    }, [notif._id])

    // Resolve course ID (encrypted)
    let courseId = null
    // Match course name in the courses list
    const matchedCourse = courses.find(
        (c) =>
            notif.notification?.includes(c.name) ||
            notif.courseName === c.name ||
            details?.courseName === c.name
    )
    if (matchedCourse) {
        courseId = matchedCourse.id
    } else {
        courseId =
            details?.courseId ||
            notif.referenceId ||
            (notif.type === 'course_enrollment' ? notif.referenceId : null)
    }

    // Determine category, icon, title, description, and click URL
    let icon = <Megaphone style={{ width: '16px', height: '16px' }} />
    let iconColor = 'var(--primary)'
    let title = 'Notification'
    let desc = notif.notification
    let isClickable = false
    let clickUrl = '#'

    if (notif.type === 'welcome') {
        icon = <Sparkles style={{ width: '16px', height: '16px' }} />
        iconColor = 'var(--primary)'
        title = 'Welcome to myAcademic'
        desc = notif.notification
    } else if (
        notif.type === 'course_enrollment' ||
        notif.notification?.includes('added to') ||
        notif.notification?.includes('removed from')
    ) {
        icon = <BookOpen style={{ width: '16px', height: '16px' }} />
        iconColor = 'var(--primary)'
        title = 'Course Update'
        desc = notif.notification
        if (
            notif.notification?.includes('added to') ||
            notif.type === 'course_enrollment'
        ) {
            isClickable = true
            clickUrl = courseId ? `/course/${courseId}/` : '#'
        }
    } else if (notif.type === 'general' || notif.type === 'live_meeting') {
        icon = <Megaphone style={{ width: '16px', height: '16px' }} />
        iconColor = 'var(--primary)'
        const courseName =
            details?.courseName || notif.courseName || 'Course Announcement'
        title = `New Announcement: "${courseName}"`
        const rawDesc =
            details?.body || details?.title || notif.notification || ''
        // Strip any residual HTML tags/base64 from description preview
        desc = rawDesc
            .replace(/<[^>]+>/g, '')
            .replace(/\[Attached Image:[^\]]+\]/g, '')
            .trim()
        isClickable = true
        clickUrl = courseId ? `/course/${courseId}/` : '#'
    } else if (notif.type === 'quiz_published') {
        icon = <AlertCircle style={{ width: '16px', height: '16px' }} />
        iconColor = 'var(--accent-red)'
        title = 'Quiz Published'
        desc = notif.notification
        isClickable = true
        clickUrl = notif.referenceId ? `/quiz/v3/${notif.referenceId}` : '#'
    } else if (notif.type === 'assignment_published') {
        icon = <Award style={{ width: '16px', height: '16px' }} />
        iconColor = 'var(--accent-green)'
        title = 'Assignment Published'
        desc = notif.notification
        isClickable = true
        clickUrl =
            courseId && notif.referenceId
                ? `/course/${courseId}/assignment/${notif.referenceId}`
                : '#'
    } else if (notif.type === 'course_ending') {
        icon = <AlertCircle style={{ width: '16px', height: '16px' }} />
        iconColor = 'var(--accent-red)'
        title = 'Course Ending Soon'
        desc = notif.notification
    } else if (notif.type === 'syllabus_updated') {
        icon = <FileText style={{ width: '16px', height: '16px' }} />
        iconColor = 'var(--primary)'
        title = 'Syllabus Updated'
        desc = notif.notification
        isClickable = true
        clickUrl = courseId ? `/course/${courseId}?tab=syllabus` : '#'
    } else if (notif.type === 'module_updated') {
        icon = <FolderOpen style={{ width: '16px', height: '16px' }} />
        iconColor = 'var(--primary)'
        title = 'Module Updated'
        desc = notif.notification
        isClickable = true
        clickUrl = courseId ? `/course/${courseId}?tab=modules` : '#'
    }

    const handleItemClick = () => {
        if (!notif.seen) {
            onMarkSeen(notif._id)
        }
        if (isClickable && clickUrl !== '#') {
            window.location.href = clickUrl
        }
    }

    const formatTime = (dateStr, notifId) => {
        let dateObj = null
        if (dateStr) {
            const parsed = new Date(dateStr)
            if (!isNaN(parsed.getTime())) {
                dateObj = parsed
            }
        }
        if (!dateObj && typeof notifId === 'string' && notifId.length >= 24) {
            const hex = notifId.substring(0, 24)
            if (/^[0-9a-fA-F]{24}$/.test(hex)) {
                const timestamp = parseInt(hex.substring(0, 8), 16) * 1000
                if (!isNaN(timestamp) && timestamp > 0) {
                    dateObj = new Date(timestamp)
                }
            }
        }
        if (!dateObj) return ''

        const diffMs = Date.now() - dateObj.getTime()
        if (diffMs < 0) return 'Just now'

        const diffMins = Math.floor(diffMs / 60000)
        const diffHours = Math.floor(diffMins / 60)
        const diffDays = Math.floor(diffHours / 24)

        if (diffMins < 1) return 'Just now'
        if (diffMins < 60) return `${diffMins}m ago`
        if (diffHours < 24) return `${diffHours}h ago`
        return `${diffDays}d ago`
    }

    return (
        <div
            className={`notification-item ${!notif.seen ? 'unread' : ''}`}
            onClick={handleItemClick}
            style={{ cursor: isClickable ? 'pointer' : 'default' }}
        >
            <div className="notif-icon-wrap" style={{ color: iconColor }}>
                {icon}
            </div>
            <div className="notif-body">
                <span className="notif-title">{title}</span>
                <span className="notif-desc">{desc}</span>
                <span className="notif-time">
                    {formatTime(notif.createdAt, notif._id)}
                </span>
            </div>
            <button
                className="notif-delete-btn"
                onClick={(e) => {
                    e.stopPropagation()
                    onDelete(notif._id)
                }}
                type="button"
            >
                <X style={{ width: '14px', height: '14px' }} />
            </button>
        </div>
    )
}

function NotificationDrawer({
    isOpen,
    onClose,
    notifications = [],
    onMarkAllRead,
    onMarkSeen,
    onDelete,
    courses = [],
}) {
    const drawerRef = useRef(null)

    useEffect(() => {
        if (!isOpen) return

        const handleOutsideClick = (event) => {
            if (event.target.closest('#bellBtn')) {
                return
            }
            if (
                drawerRef.current &&
                !drawerRef.current.contains(event.target)
            ) {
                onClose()
            }
        }

        document.addEventListener('click', handleOutsideClick, true)
        return () => {
            document.removeEventListener('click', handleOutsideClick, true)
        }
    }, [isOpen, onClose])

    return (
        <div
            ref={drawerRef}
            id="notificationDrawer"
            className={isOpen ? 'open' : ''}
            onClick={(e) => e.stopPropagation()}
        >
            <div className="drawer-header">
                <h3>Notifications</h3>
                <button
                    className="drawer-close"
                    onClick={onClose}
                    type="button"
                >
                    <X style={{ width: '20px', height: '20px' }} />
                </button>
            </div>
            <div className="drawer-actions">
                <span style={{ color: 'var(--text-muted)', fontWeight: 500 }}>
                    Recent alerts
                </span>
                <button
                    className="mark-all-read"
                    onClick={onMarkAllRead}
                    type="button"
                >
                    Mark all as read
                </button>
            </div>

            <div className="notification-list">
                {notifications.length === 0 ? (
                    <div
                        style={{
                            padding: '24px',
                            textAlign: 'center',
                            color: 'var(--text-muted)',
                        }}
                    >
                        No notifications yet.
                    </div>
                ) : (
                    notifications.map((notif) => (
                        <NotificationItem
                            key={notif._id}
                            notif={notif}
                            onMarkSeen={onMarkSeen}
                            onDelete={onDelete}
                            courses={courses}
                        />
                    ))
                )}
            </div>
        </div>
    )
}

export default NotificationDrawer
